/* eslint-disable sonarjs/no-duplicate-string */
import type { TWidget } from 'widgets/Widget';
import type { RefElement, TRefElementInstance } from 'widgets/toolbox/RefElement';
import type { TSizeConfig, ISizeChart, TSuggestedSize } from './FitGuide.types';
import { getContentByUrl, getJSONByUrl } from 'widgets/toolbox/ajax';
import { log } from 'widgets/toolbox/util';

/**
 * @description FitGuideForm implementation
 * @param Widget Base widget for extending
 * @returns Fit Guide Form class
 */
function FitGuideFormClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product/fitguide
     * @class FitGuideForm
     * @augments Widget
     * @classdesc FitGuideForm component handles the fit guide measurement form.
     * Widget manages next actions:
     * 1. Populate size options based on size chart data
     * 2. Handle user input for foot measurements (length and width)
     * 3. Calculate suggested sizes based on measurements
     * 4. Validate form inputs and display errors
     * 5. Generate product suggestion URLs with calculation results
     * 6. Manage size type selection (kids, men, women)
     * @property {string} data-widget - Widget name `fitGuideForm`
     * @property {string} data-type - Size type (kids, men, women)
     * @property {string} data-main-region - Main sizing region (EU, UK, US, JP)
     * @property {string} data-alternative-region - Alternative sizing region
     * @property {string} data-suggestion-url - URL endpoint for fetching size suggestions
     *
     * @example <caption>Example of FitGuideForm widget markup</caption>
     * <div data-widget="fitGuideForm">
     *     <select data-ref="size-options" data-type="adults" data-main-region="EU" data-alternative-region="UK">
     *         <option data-type="men">Men</option>
     *         <option data-type="women">Women</option>
     *     </select>
     *     <select data-ref="length-left">
     *         <option>Select length</option>
     *     </select>
     *     <select data-ref="width-left">
     *         <option>Select width</option>
     *     </select>
     *     <!-- Similar selects for right foot -->
     * </div>
     */
    class FitGuideForm extends Widget {
        /**
         * @description Size chart data containing measurements for different demographics
         * @type {ISizeChart}
         */
        sizeChart: ISizeChart = {
            kids: [],
            adults: {
                men: [],
                women: []
            }
        };

        /**
         * @description Product identifier for size calculations
         * @type {string}
         */
        productId = '';

        /**
         * @description Item ID for analytics tracking
         * @type {string}
         */
        itemId = '';

        /**
         * @description Item variant for analytics tracking
         * @type {string}
         */
        itemVariant = '';

        /**
         * @description Main sizing region (EU, UK, US, JP)
         * @type {string}
         */
        mainRegion = 'EU';

        /**
         * @description Alternative sizing region for display
         * @type {string}
         */
        alternativeSizing = 'UK';

        /**
         * @description Size type category (kids, adults, men, women)
         * @type {string}
         */
        sizeType = 'adults';

        /**
         * @description Indicates if the fit guide has been recalculated
         */
        recalculated: boolean = false;

        prefs(): { hidden: boolean; classesLoading: string; id: string; mode: string; categoryId: string } {
            return {
                categoryId: 'root',
                mode: 'default',
                ...super.prefs()
            };
        }

        /**
         * @description Populates select options with measurement ranges based on size chart
         * Calculates min/max values for length and width selects from size chart data
         * @param [sizeChart] - Optional size chart data to use
         * @param [productId] - Optional product ID to set
         */
        populateSelectOptions(sizeChart?: ISizeChart, productId?: string, itemId?: string, itemVariant?: string): void {
            const sizeOptions = this.ref<HTMLSelectElement>('size-options');

            this.sizeChart = sizeChart || this.sizeChart;
            this.productId = productId || this.productId;
            this.itemId = itemId || this.itemId;
            this.itemVariant = itemVariant || this.itemVariant;
            this.sizeType = sizeOptions?.data<string>('type') || this.sizeType;
            this.mainRegion = sizeOptions?.data<string>('main-region') || this.mainRegion;
            this.alternativeSizing = sizeOptions.data<string>('alternative-region') || this.alternativeSizing;

            const sizes = this.getFlattenedSizes();

            [this.ref<HTMLSelectElement>('length-left'), this.ref<HTMLSelectElement>('length-right')].forEach(select =>
                this.addOptionsToSelect(
                    select,
                    Math.min(...this.getSizeValues(sizes, 'lengthNarrow')),
                    Math.max(...this.getSizeValues(sizes, 'lengthRegular'))
                )
            );

            [this.ref<HTMLSelectElement>('width-left'), this.ref<HTMLSelectElement>('width-right')].forEach(select =>
                this.addOptionsToSelect(
                    select,
                    Math.min(...this.getSizeValues(sizes, 'widthNarrow')),
                    Math.max(...this.getSizeValues(sizes, 'widthRegular'))
                )
            );
        }

        /**
         * @description Gets the main sizing region
         * @returns The main sizing region code
         */
        getMainSize() {
            return this.mainRegion;
        }

        /**
         * @description Creates option elements for select dropdowns
         * @param initial - Initial placeholder option
         * @param min - Minimum value for options
         * @param max - Maximum value for options
         * @returns Fragment containing all option elements
         */
        getOptions(initial: HTMLOptionElement, min: number, max: number): DocumentFragment {
            const fragment = document.createDocumentFragment();

            if (initial) {
                fragment.appendChild(initial);
            }

            for (let i = min; i <= max; i++) {
                fragment.appendChild(new Option(`${i} mm`, String(i)));
            }

            return fragment;
        }

        /**
         * @description Gets flattened array of size configurations based on current size type
         * Filters sizes by main region and returns appropriate subset (kids, men, women, or all adults)
         * @returns Array of size configuration objects
         */
        getFlattenedSizes(): Array<TSizeConfig> {
            const sizeChart = this.sizeChart;
            const type = this.sizeType;

            if (!sizeChart) {
                return [];
            }

            if (type === 'kids') {
                return sizeChart.kids.filter(size => size[`size${this.mainRegion}`]);
            }

            if (type && ['men', 'women'].includes(type)) {
                return sizeChart.adults[type].filter(size => size[`size${this.mainRegion}`]);
            }

            return [...sizeChart.adults.women, ...sizeChart.adults.men].filter(size => size[`size${this.mainRegion}`]);
        }

        /**
         * @description Adds measurement options to a select element
         * @param select - The select element to populate
         * @param min - Minimum measurement value
         * @param max - Maximum measurement value
         */
        addOptionsToSelect(select: RefElement<HTMLSelectElement>, min: number, max: number) {
            const firstElement = select.get()?.options[0];

            firstElement?.removeAttribute('hidden');

            select.empty();

            const selectEl = select.get();

            if (!selectEl || !firstElement) {
                return;
            }

            selectEl.appendChild(this.getOptions(firstElement, min, max));
            selectEl.selectedIndex = 0;
        }

        /**
         * @description Extracts measurement values from size configurations
         * @param sizes - Array of size configurations
         * @param property - Property to extract values from
         * @returns Array of measurement values
         */
        getSizeValues(
            sizes: Array<TSizeConfig>,
            property: keyof Pick<TSizeConfig, 'lengthNarrow' | 'lengthRegular' | 'widthNarrow' | 'widthRegular'>
        ): Array<number> {
            return sizes.filter(item => Array.isArray(item[property])).flatMap(size => size[property]);
        }

        /**
         * @description Selects a size type option (men, women, kids) and updates the form accordingly
         * Updates the data-type attribute and repopulates measurement options
         * @param el - The clicked element
         * @param event - The click event
         */
        private selectSizeOption(el: TRefElementInstance, event: Event): void {
            const target = <HTMLSelectElement>event.target;

            this.ref<HTMLElement>('size-options').data('type', target.dataset.type);

            const sizeOptions = this.ref<HTMLSelectElement>('size-options').get()?.children;

            [...(sizeOptions || [])].forEach(option => option.classList.remove('m-selected'));

            target.classList.add('m-selected');

            this.populateSelectOptions();
        }

        /**
         * @description Handles size type option clicks (men, women, kids)
         * Updates the selected size type and repopulates measurement options
         * @param el - The clicked element
         * @param event - The click event
         */
        handleSizeOptionClick(el: TRefElementInstance, event: Event): void {
            this.selectSizeOption(el, event);
        }

        /**
         * @description Handles keydown events for size type options
         * Allows selection of size type using Enter or Space keys
         * @param el - The element receiving the keydown event
         * @param event - The keyboard event
         */
        handleKeydown(el: TRefElementInstance, event: KeyboardEvent) {
            const isEnterOrSpace = event.key === 'Enter' || event.key === ' ';

            if (isEnterOrSpace) {
                event.preventDefault();
                this.selectSizeOption(el, event);
            }
        }

        /**
         * @description Handles select dropdown changes
         * Removes error styling when a value is selected
         * @param el - The changed select element
         */
        handleSelectChange(el: TRefElementInstance): void {
            if (el.val()) {
                el.get()?.closest('.b-form_field')?.classList.remove('m-error');
            }
        }

        /**
         * @description Calculates suggested size based on foot measurements
         * @param length - Foot length measurement
         * @param width - Foot width measurement
         * @returns Suggested size object or null if no match found
         */
        getSuggestedSize(length: string, width: string) {
            if (!this.sizeChart) {
                return null;
            }

            const sizeChart = this.sizeChart;

            const sizeOptions = this.ref<HTMLSelectElement>('size-options');
            const type = sizeOptions?.data<string>('type');

            let setSizeChart: Array<TSizeConfig> = [];

            if (type === 'kids') {
                setSizeChart = sizeChart.kids;
            } else if (sizeChart.adults?.men && sizeChart.adults?.women) {
                setSizeChart = sizeChart.adults.men.concat(sizeChart.adults.women);
            }

            if (!setSizeChart?.length) {
                return null;
            }

            const region = this.mainRegion.toLowerCase();

            const filteredSizeChart = setSizeChart.filter(size => {
                return (
                    (this.inRange(Number(length), size.lengthNarrow) && this.inRange(Number(width), size.widthNarrow)) ||
                    (this.inRange(Number(length), size.lengthRegular) && this.inRange(Number(width), size.widthRegular))
                );
            });

            const mappedSizes = filteredSizeChart.map(size => {
                return {
                    eu: size.sizeEU,
                    uk: size.sizeUK,
                    us: size.sizeUS,
                    jp: size.sizeJP,
                    narrow: this.inRange(Number(width), size.widthNarrow) && this.inRange(Number(length), size.lengthNarrow),
                    regular: this.inRange(Number(width), size.widthRegular) && this.inRange(Number(length), size.lengthRegular)
                };
            });

            const suggestedSize = mappedSizes.reduce((mappedSize: TSuggestedSize | null, size: TSuggestedSize) => {
                const resultSize = mappedSize?.[region] > size[region] ? mappedSize : size;

                return resultSize;
            }, null);

            return suggestedSize;
        }

        /**
         * @description Checks if a value is within a specified range
         * @param value - Value to check
         * @param values - Range as [min, max] array
         * @returns True if value is within range
         */
        inRange(value: number, values: [number, number]): boolean {
            return value >= values[0] && value <= values[1];
        }

        /**
         * @description Calculates the best suggested size based on both feet measurements
         * Compares left and right foot calculations and returns the larger size if different
         * @param measureFields - Array of measurement input fields
         * @returns Best suggested size or null if no suitable size found
         */
        calculateSuggestedSize(measureFields: Array<TRefElementInstance>): TSuggestedSize | null {
            const [leftLength, leftWidth, rightLength, rightWidth] = measureFields.map(field => field.val());

            const leftFootResult = this.getSuggestedSize(leftLength, leftWidth);
            const rightFootResult = this.getSuggestedSize(rightLength, rightWidth);

            if (!leftFootResult) {
                return rightFootResult;
            }

            if (!rightFootResult) {
                return leftFootResult;
            }

            if (leftFootResult.eu !== rightFootResult.eu) {
                return Number(leftFootResult.eu) > Number(rightFootResult.eu) ? leftFootResult : rightFootResult;
            }

            if (leftFootResult.regular || rightFootResult.regular) {
                return leftFootResult.regular ? leftFootResult : rightFootResult;
            }

            return leftFootResult;
        }

        /**
         * @description Gets all measurement input fields in order
         * @returns Array containing [leftLength, leftWidth, rightLength, rightWidth] fields
         */
        getMeasureFields(): Array<TRefElementInstance> {
            const lengthLeft = this.ref<HTMLSelectElement>('length-left');
            const widthLeft = this.ref<HTMLSelectElement>('width-left');
            const lengthRight = this.ref<HTMLSelectElement>('length-right');
            const widthRight = this.ref<HTMLSelectElement>('width-right');

            return [lengthLeft, widthLeft, lengthRight, widthRight];
        }

        /**
         * @description Generates URL for product suggestion API call
         * @param endpoint - Base endpoint URL
         * @param sizeData - Calculated size data
         * @returns Complete URL with query parameters for API call
         */
        generateProductSuggestionUrl(endpoint, sizeData: ReturnType<FitGuideForm['calculateSuggestedSize']>): URL {
            const url = new URL(endpoint || '');

            url.searchParams.set('pid', this.productId);
            url.searchParams.set('mainSizing', this.mainRegion);
            url.searchParams.set('alternativeSizing', this.alternativeSizing);
            url.searchParams.set('calculationResult', JSON.stringify(sizeData));

            return url;
        }

        /**
         * @description Handles size calculation when form is submitted
         * Validates all measurements are provided, calculates suggested size,
         * and emits suggestion event with results
         * @param el - The calculate button element
         * @emits Event with calculation results
         */
        onCalculateSize(el: TRefElementInstance): void {
            const measureFields = this.getMeasureFields();

            if (measureFields.some(field => !field.val())) {
                measureFields.forEach((selectEl: TRefElementInstance) => {
                    if (!selectEl.val()) {
                        selectEl.get()?.closest('.b-form_field')?.classList.add('m-error');
                    }
                });

                return;
            }

            const result = this.calculateSuggestedSize(measureFields);

            this.ref('warning-form').hide();
            this.ref('recommended-size').hide();

            if (!result) {
                this.ref('warning-form').show();
                this.populateRecommendedSize({
                    leftLength: measureFields[0].val(),
                    leftWidth: measureFields[1].val(),
                    rightLength: measureFields[2].val(),
                    rightWidth: measureFields[3].val()
                });
                this.ref('recommended-size').show();

                this.populateSelectOptions(this.sizeChart);
                this.recalculated = true;

                return;
            }

            if (this.prefs().mode === 'content') {
                this.submitForContentMode(el, result, measureFields);

                return;
            }

            if (!this.productId) {
                this.emit('suggestion', {
                    noProductSuggestion: true,
                    size: result,
                    resultSize: `${result[this.mainRegion.toLowerCase()]} ${this.mainRegion.toUpperCase()}`,
                    resultAlternativeSize: `${result[this.alternativeSizing.toLowerCase()]} ${this.alternativeSizing.toUpperCase()}`,
                    selected: {
                        leftLength: measureFields[0].val(),
                        leftWidth: measureFields[1].val(),
                        rightLength: measureFields[2].val(),
                        rightWidth: measureFields[3].val()
                    }
                });

                return;
            }

            const url = this.generateProductSuggestionUrl(el.data('suggestion-url'), result);

            getContentByUrl(url.toString())
                .then((suggestionContent: string) => {
                    this.emit('suggestion', {
                        content: suggestionContent,
                        size: result,
                        resultSize: `${result[this.mainRegion.toLowerCase()]} ${this.mainRegion.toUpperCase()}`,
                        resultAlternativeSize: `${result[this.alternativeSizing.toLowerCase()]} ${this.alternativeSizing.toUpperCase()}`,
                        selected: {
                            leftLength: measureFields[0].val(),
                            leftWidth: measureFields[1].val(),
                            rightLength: measureFields[2].val(),
                            rightWidth: measureFields[3].val()
                        }
                    });

                    const eventName = this.recalculated ? 'display_result_recalculated' : 'display_result';

                    this.eventBus().emit('gtm.event', {
                        event_name: eventName,
                        [eventName]: {
                            feature: 'fit_guide',
                            item_id: this.itemId || this.productId,
                            item_variant: this.itemVariant?.toString() || '',
                            value_dim: `${result.eu}_${result.narrow ? 'narrow' : 'regular'}`
                        }
                    });

                    this.itemVariant = '';
                })
                .catch(err => {
                    if (!PRODUCTION) {
                        log.error('Failed to getContentByUrl for suggestion view: ' + err, this);
                    }
                });
        }

        /**
         * @description Submits the calculated size for content mode
         * Fetches suggestion data from the server and emits the suggestion event
         * @param el - The calculate button element
         * @param result - The calculated suggested size
         * @param measureFields - Array of measurement input fields
         */
        submitForContentMode(el: TRefElementInstance, result: TSuggestedSize, measureFields: Array<TRefElementInstance>) {
            getJSONByUrl(el.data('suggestion-url'), {
                resultSize: `${result[this.mainRegion.toLowerCase()]};${result[this.alternativeSizing.toLowerCase()]}`,
                narrow: String(result.narrow),
                cgid: this.prefs().categoryId
            })
                .then(response => {
                    this.emit('suggestion', {
                        noExistingSize: response?.noExistingSize,
                        showResultsURL: response?.url,
                        size: result,
                        resultSize: `${result[this.mainRegion.toLowerCase()]} ${this.mainRegion.toUpperCase()}`,
                        resultAlternativeSize: `${result[this.alternativeSizing.toLowerCase()]} ${this.alternativeSizing.toUpperCase()}`,
                        selected: {
                            leftLength: measureFields[0].val(),
                            leftWidth: measureFields[1].val(),
                            rightLength: measureFields[2].val(),
                            rightWidth: measureFields[3].val()
                        }
                    });
                })
                .catch(err => {
                    if (!PRODUCTION) {
                        log.error('Failed to submitFormJson for suggestion view: ' + err, this);
                    }
                });
        }

        /**
         * @description Populates recommended size information in the UI
         * Updates text elements with provided measurement values
         * @param size - Object containing left and right foot measurements
         */
        populateRecommendedSize(size: { leftLength: string; leftWidth: string; rightLength: string; rightWidth: string }) {
            this.ref('left-length').setText(`${size.leftLength} mm`);
            this.ref('right-length').setText(`${size.rightLength} mm`);
            this.ref('left-width').setText(`${size.leftWidth} mm`);
            this.ref('right-width').setText(`${size.rightWidth} mm`);
        }
    }

    return FitGuideForm;
}

export type TFitGuideForm = ReturnType<typeof FitGuideFormClassCreator>;

export type TFitGuideFormInstance = InstanceType<TFitGuideForm>;

export default FitGuideFormClassCreator;
